<template>
  <app-content title="记忆候选管理">
    <div class="candidate-list-page">

      <!-- 过滤栏 -->
      <div class="filter-bar">
        <div class="filter-item">
          <label>状态</label>
          <select v-model="filter.status" class="filter-select" @change="loadCandidates">
            <option value="">全部</option>
            <option value="CANDIDATE">候选</option>
            <option value="EXCLUDED">已排除</option>
            <option value="READY_FOR_EXTRACTION">待提取</option>
            <option value="EXTRACTED">已提取</option>
            <option value="FAILED">失败</option>
          </select>
        </div>
        <div class="filter-item">
          <label>行为类型</label>
          <input v-model="filter.behaviorType" type="text" class="filter-input" placeholder="如 FUND_FLOW" @change="loadCandidates" />
        </div>
        <div class="filter-item">
          <label>来源渠道</label>
          <input v-model="filter.sourceProfileKey" type="text" class="filter-input" placeholder="如 fund_flow:* / bank_flow:*" @change="loadCandidates" />
        </div>
        <div class="filter-item">
          <label>交易类型</label>
          <input v-model="filter.normalizedTxnType" type="text" class="filter-input" placeholder="如 PAYMENT_FEE" @change="loadCandidates" />
        </div>
        <button class="btn btn-primary btn-sm" @click="loadCandidates"><i class="h-icon-search"></i> 查询</button>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading">加载中...</div>

      <!-- 错误提示 -->
      <div v-if="loadError" class="error-msg"><i class="h-icon-warning"></i> {{ loadError }}</div>

      <!-- 空状态 -->
      <div v-if="!loading && !loadError && candidates.length === 0" class="empty-state">
        <i class="h-icon-inbox"></i>
        <p>暂无记忆候选记录</p>
        <p class="empty-hint">完成业务转凭证并提交审核后，符合条件的记录会自动成为候选。</p>
      </div>

      <!-- 候选列表 -->
      <table v-if="candidates.length > 0" class="candidate-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>状态</th>
            <th>行为类型</th>
            <th>交易类型</th>
            <th>方向</th>
            <th>对手方</th>
            <th>现金科目</th>
            <th>置信度</th>
            <th>风险标记</th>
            <th>更新时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in candidates" :key="c.detailId">
            <td class="mono">{{ c.detailId }}</td>
            <td><span class="status-badge" :class="statusClass(c.status)">{{ statusLabel(c.status) }}</span></td>
            <td>{{ c.behaviorType || '-' }}</td>
            <td>{{ c.normalizedTxnType || '-' }}</td>
            <td>{{ c.direction || '-' }}</td>
            <td>{{ c.counterpartyRole || '-' }}</td>
            <td class="mono">{{ c.cashSubjectSignature || '-' }}</td>
            <td>{{ formatConfidence(c.qualityScore) }}</td>
            <td>
              <span v-if="c.riskFlags && c.riskFlags.length">
                <span v-for="flag in c.riskFlags" :key="flag" class="risk-tag">{{ flag }}</span>
              </span>
              <span v-else class="no-risk">-</span>
            </td>
            <td class="mono">{{ formatTime(c.updatedAt) }}</td>
            <td class="action-cell">
              <button class="action-btn action-detail" @click="handleViewDetail(c)">详情</button>
              <template v-if="c.status === 'CANDIDATE'">
                <button class="action-btn action-exclude" @click="handleExclude(c)">排除</button>
                <button class="action-btn action-ready" @click="handleMarkReady(c)">标记 ready</button>
              </template>
              <template v-else-if="c.status === 'EXCLUDED'">
                <button class="action-btn action-restore" @click="handleRestore(c)">恢复</button>
              </template>
              <template v-else-if="c.status === 'READY_FOR_EXTRACTION'">
                <button class="action-btn action-exclude" @click="handleExclude(c)">排除</button>
              </template>
              <template v-else>
                <span class="no-action">-</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 操作反馈 -->
      <div v-if="actionMsg" class="action-msg" :class="actionMsgType">{{ actionMsg }}</div>
    </div>

    <!-- 详情抽屉 -->
    <div v-if="detailDrawerVisible" class="detail-overlay" @click.self="closeDetail">
      <div class="detail-drawer">
        <div class="detail-header">
          <h3>候选详情 #{{ currentDetail.detailId }}</h3>
          <button class="detail-close" @click="closeDetail">&times;</button>
        </div>
        <div v-if="detailLoading" class="loading">加载中...</div>
        <div v-else class="detail-body">
          <!-- 基本信息 -->
          <section class="detail-section">
            <h4 class="section-title">基本信息</h4>
            <table class="detail-table">
              <tr><td class="label">ID</td><td class="mono">{{ currentDetail.detailId }}</td></tr>
              <tr><td class="label">状态</td><td><span class="status-badge" :class="statusClass(currentDetail.status)">{{ statusLabel(currentDetail.status) }}</span></td></tr>
              <tr><td class="label">行为类型</td><td>{{ currentDetail.behaviorType || '-' }}</td></tr>
              <tr><td class="label">来源渠道</td><td>{{ currentDetail.sourceProfileKey || '-' }}</td></tr>
              <tr><td class="label">交易类型</td><td>{{ currentDetail.normalizedTxnType || '-' }}</td></tr>
              <tr><td class="label">方向</td><td>{{ currentDetail.direction || '-' }}</td></tr>
              <tr><td class="label">对手方</td><td>{{ currentDetail.counterpartyRole || '-' }}</td></tr>
              <tr><td class="label">现金科目</td><td class="mono">{{ currentDetail.cashSubjectSignature || '-' }}</td></tr>
              <tr><td class="label">置信度</td><td>{{ formatConfidence(currentDetail.qualityScore) }}</td></tr>
            </table>
          </section>

          <!-- 来源信息 -->
          <section class="detail-section">
            <h4 class="section-title">来源信息</h4>
            <table class="detail-table">
              <tr><td class="label">决策 ID</td><td class="mono">{{ currentDetail.sourceDecisionId || '-' }}</td></tr>
              <tr><td class="label">凭证草稿 ID</td><td class="mono">{{ currentDetail.sourceVoucherDraftId || '-' }}</td></tr>
              <tr><td class="label">关系签名</td><td class="mono">{{ currentDetail.relationSignature || '-' }}</td></tr>
              <tr><td class="label">事实谓词签名</td><td class="mono">{{ currentDetail.factPredicateSignature || '-' }}</td></tr>
              <tr><td class="label">摘要语义签名</td><td class="mono">{{ currentDetail.summarySemanticSignature || '-' }}</td></tr>
            </table>
          </section>

          <!-- 风险与排除 -->
          <section class="detail-section">
            <h4 class="section-title">风险与排除</h4>
            <table class="detail-table">
              <tr>
                <td class="label">风险标记</td>
                <td>
                  <span v-if="currentDetail.riskFlags && currentDetail.riskFlags.length">
                    <span v-for="flag in currentDetail.riskFlags" :key="flag" class="risk-tag">{{ flag }}</span>
                  </span>
                  <span v-else class="no-risk">-</span>
                </td>
              </tr>
              <tr>
                <td class="label">排除原因</td>
                <td>
                  <span v-if="currentDetail.exclusionReasons && currentDetail.exclusionReasons.length">
                    <span v-for="reason in currentDetail.exclusionReasons" :key="reason" class="exclusion-tag">{{ reason }}</span>
                  </span>
                  <span v-else class="no-risk">-</span>
                </td>
              </tr>
            </table>
          </section>

          <!-- 审计追踪 -->
          <section class="detail-section">
            <h4 class="section-title">审计追踪</h4>
            <table class="detail-table">
              <tr><td class="label">创建时间</td><td class="mono">{{ formatTime(currentDetail.createdAt) }}</td></tr>
              <tr><td class="label">更新时间</td><td class="mono">{{ formatTime(currentDetail.updatedAt) }}</td></tr>
              <tr><td class="label">最后操作</td><td>{{ currentDetail.lastAction || '-' }}</td></tr>
              <tr><td class="label">操作人</td><td>{{ currentDetail.lastActionBy || '-' }}</td></tr>
              <tr><td class="label">操作时间</td><td class="mono">{{ formatTime(currentDetail.lastActionAt) }}</td></tr>
              <tr><td class="label">操作备注</td><td>{{ currentDetail.lastActionComment || '-' }}</td></tr>
              <tr><td class="label">变更前状态</td><td><span v-if="currentDetail.previousStatus" class="status-badge" :class="statusClass(currentDetail.previousStatus)">{{ statusLabel(currentDetail.previousStatus) }}</span><span v-else>-</span></td></tr>
            </table>
          </section>

          <!-- 确认分录模式 -->
          <section v-if="currentDetail.confirmedLinePatterns && currentDetail.confirmedLinePatterns.length" class="detail-section">
            <h4 class="section-title">确认分录模式 ({{ currentDetail.confirmedLinePatterns.length }} 条)</h4>
            <table class="detail-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>方向</th>
                  <th>科目模板</th>
                  <th>金额绑定</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(lp, idx) in currentDetail.confirmedLinePatterns" :key="idx">
                  <td>{{ lp.entryNo || idx + 1 }}</td>
                  <td>{{ lp.side || '-' }}</td>
                  <td class="mono">{{ lp.subjectTemplate || '-' }}</td>
                  <td>{{ lp.amountBinding || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>
      </div>
    </div>

    <!-- 操作确认对话框 -->
    <div v-if="commentDialog.visible" class="detail-overlay" @click.self="cancelCommentDialog">
      <div class="confirm-dialog">
        <div class="detail-header">
          <h3>{{ commentDialog.title }}</h3>
          <button class="detail-close" @click="cancelCommentDialog">&times;</button>
        </div>
        <div class="confirm-body">
          <p class="confirm-message">{{ commentDialog.message }}</p>
          <textarea
            v-model="commentDialog.comment"
            class="comment-textarea"
            :placeholder="commentDialog.placeholder"
            :class="{ 'input-error': commentDialog.showError }"
            rows="3"
          ></textarea>
          <p v-if="commentDialog.showError" class="field-error">请填写操作原因</p>
        </div>
        <div class="confirm-footer">
          <button class="btn btn-default btn-sm" @click="cancelCommentDialog">取消</button>
          <button class="btn btn-primary btn-sm" :disabled="commentDialog.submitting" @click="confirmCommentAction">
            {{ commentDialog.submitting ? '提交中...' : '确认' }}
          </button>
        </div>
      </div>
    </div>
  </app-content>
</template>

<script>
export default {
  name: 'DecisionMemoryCandidateList',
  data() {
    return {
      filter: {
        status: '',
        behaviorType: '',
        sourceProfileKey: '',
        normalizedTxnType: ''
      },
      candidates: [],
      loading: false,
      loadError: '',
      actionMsg: '',
      actionMsgType: 'success',
      detailDrawerVisible: false,
      currentDetail: {},
      detailLoading: false,
      commentDialog: {
        visible: false,
        operation: '',
        target: null,
        comment: '',
        required: false,
        title: '',
        message: '',
        placeholder: '',
        submitting: false,
        showError: false
      }
    };
  },
  created() {
    this.loadCandidates();
  },
  methods: {
    async loadCandidates() {
      this.loading = true;
      this.loadError = '';
      try {
        var params = {};
        if (this.filter.status) params.status = this.filter.status;
        if (this.filter.behaviorType) params.behaviorType = this.filter.behaviorType;
        if (this.filter.sourceProfileKey) params.sourceProfileKey = this.filter.sourceProfileKey;
        if (this.filter.normalizedTxnType) params.normalizedTxnType = this.filter.normalizedTxnType;
        var resp = await this.$api.decisionMemory.listCandidates(params);
        if (resp && resp.code === 200) {
          this.candidates = resp.data || [];
        } else {
          this.loadError = (resp && resp.msg) || '加载候选列表失败';
        }
      } catch (e) {
        this.loadError = (e && e.msg) || '加载候选列表失败';
      } finally {
        this.loading = false;
      }
    },

    async handleViewDetail(c) {
      this.detailDrawerVisible = true;
      this.detailLoading = true;
      this.currentDetail = {};
      try {
        var resp = await this.$api.decisionMemory.getCandidate(c.detailId);
        if (resp && resp.code === 200) {
          this.currentDetail = resp.data || {};
        } else {
          this.currentDetail = { detailId: c.detailId, _loadError: (resp && resp.msg) || '加载详情失败' };
        }
      } catch (e) {
        this.currentDetail = { detailId: c.detailId, _loadError: '加载详情失败' };
      } finally {
        this.detailLoading = false;
      }
    },

    closeDetail() {
      this.detailDrawerVisible = false;
      this.currentDetail = {};
    },

    handleExclude(c) {
      this.commentDialog = {
        visible: true,
        operation: 'exclude',
        target: c,
        comment: '',
        required: true,
        title: '排除候选',
        message: '请确认排除候选 #' + c.detailId + '。被排除的候选将不会再进入抽取流程。',
        placeholder: '请输入排除原因，例如：一次性特殊处理 / 对手方未知 / 事实冲突 / 不适合沉淀为记忆',
        submitting: false,
        showError: false
      };
    },

    handleRestore(c) {
      this.commentDialog = {
        visible: true,
        operation: 'restore',
        target: c,
        comment: '',
        required: false,
        title: '恢复候选',
        message: '确认将候选 #' + c.detailId + ' 恢复为候选状态？恢复后等待后续抽取评估。',
        placeholder: '恢复为候选，等待后续抽取评估（可选）',
        submitting: false,
        showError: false
      };
    },

    handleMarkReady(c) {
      this.commentDialog = {
        visible: true,
        operation: 'mark-ready',
        target: c,
        comment: '',
        required: false,
        title: '标记候选为待提取',
        message: '确认将候选 #' + c.detailId + ' 标记为待提取？标记后该候选将进入后续抽取流程。',
        placeholder: '请说明为什么该候选已准备进入后续抽取流程（可选）',
        submitting: false,
        showError: false
      };
    },

    cancelCommentDialog() {
      this.commentDialog.visible = false;
    },

    async confirmCommentAction() {
      var dialog = this.commentDialog;
      if (dialog.required && !dialog.comment.trim()) {
        dialog.showError = true;
        return;
      }
      dialog.showError = false;
      dialog.submitting = true;

      this.clearAction();
      var c = dialog.target;
      var comment = (dialog.comment || '').trim();

      try {
        var resp;
        if (dialog.operation === 'exclude') {
          resp = await this.$api.decisionMemory.excludeCandidate(c.detailId, comment);
        } else if (dialog.operation === 'restore') {
          resp = await this.$api.decisionMemory.restoreCandidate(c.detailId, comment);
        } else if (dialog.operation === 'mark-ready') {
          resp = await this.$api.decisionMemory.markReadyCandidate(c.detailId, comment);
        }

        if (resp && resp.code === 200) {
          var msgMap = {
            'exclude': '已排除候选 #' + c.detailId,
            'restore': '已恢复候选 #' + c.detailId,
            'mark-ready': '已标记候选 #' + c.detailId + ' 为待提取'
          };
          this.showAction(msgMap[dialog.operation] || '操作成功', 'success');
          this.commentDialog.visible = false;
          this.loadCandidates();
        } else {
          this.showAction((resp && resp.msg) || '操作失败', 'error');
        }
      } catch (e) {
        this.showAction('操作失败', 'error');
      } finally {
        dialog.submitting = false;
      }
    },

    statusClass(status) {
      var map = {
        'CANDIDATE': 'badge-candidate',
        'EXCLUDED': 'badge-excluded',
        'READY_FOR_EXTRACTION': 'badge-ready',
        'EXTRACTED': 'badge-extracted',
        'FAILED': 'badge-failed'
      };
      return map[status] || '';
    },

    statusLabel(status) {
      var map = {
        'CANDIDATE': '候选',
        'EXCLUDED': '已排除',
        'READY_FOR_EXTRACTION': '待提取',
        'EXTRACTED': '已提取',
        'FAILED': '失败'
      };
      return map[status] || status;
    },

    formatConfidence(v) {
      if (v === null || v === undefined) return '-';
      var n = typeof v === 'string' ? parseFloat(v) : v;
      return (n * 100).toFixed(0) + '%';
    },

    formatTime(ts) {
      if (!ts) return '-';
      return ts.substring(0, 19).replace('T', ' ');
    },

    showAction(msg, type) {
      this.actionMsg = msg;
      this.actionMsgType = type;
    },

    clearAction() {
      this.actionMsg = '';
    }
  }
};
</script>

<style scoped>
.candidate-list-page {
  padding: 16px;
  font-size: 13px;
}

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-item label {
  font-size: 12px;
  color: #666;
}

.filter-select, .filter-input {
  height: 30px;
  padding: 0 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  min-width: 140px;
}

.filter-input {
  min-width: 160px;
}

.candidate-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.candidate-table th {
  background: #fafafa;
  padding: 8px;
  text-align: left;
  border-bottom: 2px solid #e8e8e8;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.candidate-table td {
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.candidate-table tbody tr:hover td {
  background: #fafafa;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
}

.badge-candidate { background: #1890ff; }
.badge-excluded { background: #ff4d4f; }
.badge-ready { background: #52c41a; }
.badge-extracted { background: #722ed1; }
.badge-failed { background: #faad14; color: #333; }

.mono {
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 11px;
}

.risk-tag {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  background: #fff2f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
  margin: 1px;
}

.exclusion-tag {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  background: #fff7e6;
  color: #fa8c16;
  border: 1px solid #ffd591;
  margin: 1px;
}

.no-risk, .no-action {
  color: #ccc;
}

.action-cell {
  white-space: nowrap;
}

.action-btn {
  padding: 3px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 4px;
  background: #fff;
  color: #333;
}

.action-btn:hover { opacity: 0.8; }

.action-exclude { color: #ff4d4f; border-color: #ffccc7; }
.action-exclude:hover { background: #fff2f0; }

.action-restore { color: #1890ff; border-color: #91d5ff; }
.action-restore:hover { background: #e6f7ff; }

.action-ready { color: #52c41a; border-color: #b7eb8f; }
.action-ready:hover { background: #f6ffed; }

.action-detail { color: #722ed1; border-color: #d3adf7; }
.action-detail:hover { background: #f9f0ff; }

.loading, .empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.empty-state i {
  font-size: 48px;
  color: #d9d9d9;
  margin-bottom: 12px;
}

.empty-state p {
  margin: 4px 0;
}

.empty-hint {
  font-size: 12px;
  color: #bbb;
}

.error-msg {
  padding: 10px 16px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  color: #ff4d4f;
  margin-bottom: 12px;
  font-size: 13px;
}

.action-msg {
  position: fixed;
  top: 60px;
  right: 20px;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 13px;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
}

.action-msg.success {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  color: #52c41a;
}

.action-msg.error {
  background: #fff2f0;
  border: 1px solid #ffccc7;
  color: #ff4d4f;
}

/* Detail drawer */
.detail-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.35);
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
}

.detail-drawer {
  width: 640px;
  max-width: 90vw;
  height: 100%;
  background: #fff;
  overflow-y: auto;
  box-shadow: -4px 0 12px rgba(0,0,0,0.12);
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.detail-header h3 {
  margin: 0;
  font-size: 15px;
  color: #333;
}

.detail-close {
  border: none;
  background: none;
  font-size: 22px;
  cursor: pointer;
  color: #999;
  line-height: 1;
}

.detail-close:hover { color: #333; }

.detail-body {
  padding: 16px 20px 40px;
}

.detail-section {
  margin-bottom: 20px;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin: 0 0 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.detail-table td, .detail-table th {
  padding: 6px 8px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: top;
}

.detail-table th {
  background: #fafafa;
  font-weight: 600;
  color: #555;
  text-align: left;
}

.detail-table .label {
  width: 120px;
  color: #888;
  font-size: 12px;
  white-space: nowrap;
}

/* Confirm dialog */
.confirm-dialog {
  width: 420px;
  max-width: 90vw;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.18);
  margin: auto;
  align-self: center;
}

.confirm-body {
  padding: 16px 20px;
}

.confirm-message {
  margin: 0 0 12px;
  font-size: 13px;
  color: #555;
  line-height: 1.6;
}

.comment-textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;
  line-height: 1.5;
}

.comment-textarea:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24,144,255,0.15);
}

.comment-textarea.input-error {
  border-color: #ff4d4f;
}

.field-error {
  margin: 4px 0 0;
  font-size: 12px;
  color: #ff4d4f;
}

.confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  border-radius: 0 0 6px 6px;
}

.btn-default {
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #333;
}

.btn-default:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.btn-primary {
  background: #1890ff;
  border: 1px solid #1890ff;
  color: #fff;
}

.btn-primary:hover {
  opacity: 0.85;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sm {
  padding: 4px 14px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

.detail-overlay {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 80px;
}
</style>
